import React, { useState, useRef, useEffect } from 'react';
import { Settings } from 'lucide-react';
import type { EmpresaSelectorProps } from './types';
import { Button } from '../../components/Button';
import Tooltip from '../../components/Tooltip/Tooltip';
import { Badge } from '../../components/Badge/Badge';

const DEMO_RUC = '20000000000';

export const EmpresaSelector: React.FC<EmpresaSelectorProps> = ({
  actual,
  empresas = [],
  sedes = [],
  onChangeWorkspace,
  onAdministrarEmpresas,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedEmpresa, setExpandedEmpresa] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Cerrar al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
    setSearchQuery('');
    setExpandedEmpresa(null);
  };

  const handleSelectWorkspace = (empresaId: string, sedeId: string) => {
    onChangeWorkspace?.(empresaId, sedeId);
    setIsOpen(false);
  };

  // Filtrar empresas por búsqueda
  const filteredEmpresas = empresas.filter(empresa =>
    empresa &&
    (empresa.nombre?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    empresa.ruc?.includes(searchQuery))
  );

  // Obtener otras sedes de la empresa actual (excluyendo la actual)
  const otrasSedes = actual?.empresa?.id && actual?.sede?.id ? sedes.filter(
    sede => sede && sede.empresaId === actual.empresa.id && sede.id !== actual.sede.id && sede.esActivo === true
  ) : [];

  // Verificar si hay otras empresas disponibles (excluyendo la actual)
  const otrasEmpresas = actual?.empresa?.id ? filteredEmpresas.filter(emp => emp.id !== actual.empresa.id) : filteredEmpresas;
  const otrasEmpresasReales = otrasEmpresas.filter(emp => emp.ruc !== DEMO_RUC);
  const empresaDemoOtra = otrasEmpresas.find(emp => emp.ruc === DEMO_RUC) ?? null;

  if (!actual) return null;

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <Tooltip
        text={`${actual?.empresa?.nombre || ''}\n${actual?.sede?.nombre || ''}\n${actual?.empresa?.ruc ? `RUC: ${actual.empresa.ruc}` : ''}`}
        position="bottom"
        size="lg"
        disabled={isOpen}
      >
        {/* Trigger Button */}
        <button id="empresa-selector-toggle" name="empresaSelectorToggle" type="button"
          onClick={handleToggle}
          className="h-10 flex items-center gap-2.5 px-3 bg-surface-2/30 hover:bg-surface-hover rounded-lg cursor-pointer transition-all duration-200"
          aria-expanded={isOpen}
          aria-haspopup="true"
        >
        {/* Logo */}
        <div 
          className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm"
          style={{ background: actual?.empresa?.gradient || 'linear-gradient(135deg, #1EBFA2 0%, #16A085 100%)' }}
        >
          <svg width="16" height="16" fill="white" viewBox="0 0 24 24" strokeWidth="0">
            <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
            <path d="M9 22V12h6v10"/>
          </svg>
        </div>

        {/* Separador */}
        <div className="h-6 w-px bg-secondary/30"></div>

        {/* Textos */}
        <div className="flex flex-col items-start justify-center min-w-0 max-w-[200px] pr-1 gap-0.5">
          <div className="text-[13px] font-semibold text-primary leading-tight w-full text-left truncate">
            {actual?.empresa?.nombre || ''}
          </div>
          <div className="text-[10px] text-secondary leading-tight w-full text-left truncate">
            {actual?.sede?.nombre || ''}
          </div>
        </div>

        {/* Chevron */}
        <svg 
          width="14" 
          height="14" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24" 
          className={`text-secondary flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
        </svg>
      </button>
      </Tooltip>

      {/* Dropdown */}
      {isOpen && (
        <div 
          className="absolute top-full left-0 mt-2 w-[352px] max-h-[480px] bg-surface-0 border border-secondary rounded-xl shadow-lg z-[1000] overflow-auto"
          style={{
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}
        >
          {/* Búsqueda */}
          <div className="p-3 border-b border-secondary sticky top-0 bg-surface-0 z-10">
            <div className="relative">
              <svg 
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-tertiary" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth="2" 
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input 
                id="empresa-selector-buscar"
                name="empresaSelectorBuscar"
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar empresa o establecimiento..."
                className="w-full h-9 pl-9 pr-3 bg-surface-2 border border-secondary rounded-lg text-sm text-primary placeholder-tertiary focus:outline-none focus:border-primary"
              />
            </div>
          </div>

          {/* Contenido */}
          <div className="py-2">
            {/* Empresa y Sede Actual */}
            <div className="px-4 pt-2 pb-1">
              <div className="text-[10px] font-semibold text-tertiary uppercase tracking-wide">
                Actual
              </div>
            </div>
            
            <div className="px-3 pb-3">
              <div className="p-3 bg-primary-light dark:bg-surface-2 border-[1.5px] border-primary rounded-lg">
                <div className="flex items-center gap-2.5 mb-2">
                  <div 
                    className="w-8 h-8 rounded-md flex items-center justify-center"
                    style={{ background: actual.empresa?.gradient || 'linear-gradient(135deg, #1EBFA2 0%, #16A085 100%)' }}
                  >
                    <svg width="16" height="16" fill="white" viewBox="0 0 24 24" strokeWidth="0">
                      <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
                      <path d="M9 22V12h6v10"/>
                    </svg>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <span className="text-sm font-semibold text-primary">
                        {actual?.empresa?.nombre || ''}
                      </span>
                      {actual?.empresa?.ruc === DEMO_RUC && (
                        <Badge variant="amber" size="sm" noDot>Demo</Badge>
                      )}
                    </div>
                    {actual?.empresa?.ruc && (
                      <div className="text-[11px] text-secondary">
                        RUC: {actual.empresa.ruc}
                      </div>
                    )}
                  </div>
                </div>
                <div className="pl-[42px]">
                  <div className="text-[13px] font-medium text-primary mb-0.5 flex items-center gap-1">
                    <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                    </svg>
                    {actual?.sede?.nombre || ''}
                  </div>
                  {actual?.sede?.direccion && (
                    <div className="text-[11px] text-secondary pl-4">
                      {actual.sede.direccion}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Otras Sedes de la Empresa Actual */}
            {otrasSedes.length > 0 && (
              <>
                <div className="px-4 pt-1 pb-1">
                  <div className="text-[10px] font-semibold text-tertiary uppercase tracking-wide">
                    Otros establecimientos
                  </div>
                </div>
                
                <div className="px-4 space-y-1 pb-3">
                  {otrasSedes.map((sede) => (
                    <button id={`empresa-selector-sede-${sede.id}`} name={`empresaSelectorSede${sede.id}`} type="button"
                      key={sede.id}
                      onClick={() => handleSelectWorkspace(actual.empresa.id, sede.id)}
                      className="w-full py-2.5 px-3 rounded-lg hover:bg-surface-hover cursor-pointer transition-all text-left"
                    >
                      <div className="text-[13px] font-medium text-primary mb-0.5 flex items-center gap-1">
                        <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                        </svg>
                        {sede.nombre}
                      </div>
                      {sede.direccion && (
                        <div className="text-[11px] text-secondary pl-4">
                          {sede.direccion}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </>
            )}

            {/* Cambiar a Otras Empresas (reales) */}
            {otrasEmpresasReales.length > 0 && (
              <>
                <div className="px-4 pt-1 pb-1">
                  <div className="text-[10px] font-semibold text-tertiary uppercase tracking-wide">
                    {otrasSedes.length > 0 ? 'Cambiar a otra empresa' : 'Cambiar a'}
                  </div>
                </div>

                <div className="px-4 pb-2">
                  {otrasEmpresasReales.map((empresa) => {
                    const empresaSedes = sedes.filter(
                      sede => sede && sede.empresaId === empresa.id && sede.esActivo === true
                    );
                    const isExpanded = expandedEmpresa === empresa.id;

                    return (
                      <div key={empresa.id}>
                        <button id={`empresa-selector-empresa-${empresa.id}`} name={`empresaSelectorEmpresa${empresa.id}`} type="button"
                          onClick={() => setExpandedEmpresa(isExpanded ? null : empresa.id)}
                          className="w-full flex items-center gap-2.5 py-2.5 cursor-pointer hover:bg-surface-hover rounded-lg px-2 -mx-2 transition-colors"
                        >
                          <div
                            className="w-7 h-7 rounded-md flex items-center justify-center"
                            style={{ background: empresa.gradient || 'linear-gradient(135deg, #1EBFA2 0%, #16A085 100%)' }}
                          >
                            <svg width="14" height="14" fill="white" viewBox="0 0 24 24" strokeWidth="0">
                              <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
                              <path d="M9 22V12h6v10"/>
                            </svg>
                          </div>
                          <div className="flex-1 text-left">
                            <div className="text-[13px] font-semibold text-primary">
                              {empresa.nombre}
                              {empresaSedes.length > 0 && (
                                <span className="inline-block ml-1.5 px-1.5 py-0.5 bg-success-bg text-success rounded text-[10px] font-semibold">
                                  {empresaSedes.length} {empresaSedes.length === 1 ? 'establecimiento' : 'establecimientos'}
                                </span>
                              )}
                            </div>
                          </div>
                          {empresaSedes.length > 0 && (
                            <svg
                              width="14"
                              height="14"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              className={`text-tertiary transition-transform duration-200 ${
                                isExpanded ? 'rotate-180' : ''
                              }`}
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
                            </svg>
                          )}
                        </button>

                        {/* Sedes expandibles */}
                        {isExpanded && empresaSedes.length > 0 && (
                          <div className="pl-[38px] mt-1 mb-2 space-y-1">
                            {empresaSedes.map(sede => (
                              <button id={`empresa-selector-empresa-sede-${sede.id}`} name={`empresaSelectorEmpresaSede${sede.id}`} type="button"
                                key={sede.id}
                                onClick={() => handleSelectWorkspace(empresa.id, sede.id)}
                                className="w-full py-2 px-3 rounded-lg border-l-2 border-secondary hover:border-primary hover:bg-surface-hover cursor-pointer transition-all text-left"
                              >
                                <div className="text-xs font-medium text-primary mb-0.5">
                                  {sede.nombre}
                                </div>
                                {sede.direccion && (
                                  <div className="text-[11px] text-secondary flex items-center gap-1">
                                    <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                                    </svg>
                                    {sede.direccion}
                                  </div>
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {/* Empresa demo al fondo con separador */}
            {empresaDemoOtra && (
              <>
                <div className="mx-3 border-t border-secondary" />
                <div className="px-4 pt-3 pb-1">
                  <div className="text-[10px] font-semibold text-tertiary uppercase tracking-wide">
                    Empresa de prueba
                  </div>
                </div>
                <div className="px-4 pb-2">
                  {(() => {
                    const empresaSedes = sedes.filter(
                      sede => sede && sede.empresaId === empresaDemoOtra.id && sede.esActivo === true
                    );
                    const isExpanded = expandedEmpresa === empresaDemoOtra.id;
                    return (
                      <div>
                        <button id={`empresa-selector-demo-${empresaDemoOtra.id}`} name={`empresaSelectorDemo${empresaDemoOtra.id}`} type="button"
                          onClick={() => setExpandedEmpresa(isExpanded ? null : empresaDemoOtra.id)}
                          className="w-full flex items-center gap-2.5 py-2.5 cursor-pointer hover:bg-surface-hover rounded-lg px-2 -mx-2 transition-colors opacity-70 hover:opacity-100"
                        >
                          <div
                            className="w-7 h-7 rounded-md flex items-center justify-center"
                            style={{ background: empresaDemoOtra.gradient || 'linear-gradient(135deg, #1EBFA2 0%, #16A085 100%)' }}
                          >
                            <svg width="14" height="14" fill="white" viewBox="0 0 24 24" strokeWidth="0">
                              <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
                              <path d="M9 22V12h6v10"/>
                            </svg>
                          </div>
                          <div className="flex-1 text-left">
                            <div className="flex items-center gap-1.5">
                              <span className="text-[13px] font-semibold text-primary">{empresaDemoOtra.nombre}</span>
                              <Badge variant="amber" size="sm" noDot>Demo</Badge>
                            </div>
                          </div>
                          {empresaSedes.length > 0 && (
                            <svg
                              width="14"
                              height="14"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              className={`text-tertiary transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
                            </svg>
                          )}
                        </button>
                        {isExpanded && empresaSedes.length > 0 && (
                          <div className="pl-[38px] mt-1 mb-2 space-y-1">
                            {empresaSedes.map(sede => (
                              <button id={`empresa-selector-demo-sede-${sede.id}`} name={`empresaSelectorDemoSede${sede.id}`} type="button"
                                key={sede.id}
                                onClick={() => handleSelectWorkspace(empresaDemoOtra.id, sede.id)}
                                className="w-full py-2 px-3 rounded-lg border-l-2 border-secondary hover:border-primary hover:bg-surface-hover cursor-pointer transition-all text-left"
                              >
                                <div className="text-xs font-medium text-primary mb-0.5">{sede.nombre}</div>
                                {sede.direccion && (
                                  <div className="text-[11px] text-secondary flex items-center gap-1">
                                    <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                                    </svg>
                                    {sede.direccion}
                                  </div>
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </>
            )}

            {/* Sin resultados */}
            {searchQuery && filteredEmpresas.length === 0 && (
              <div className="px-4 py-8 text-center">
                <p className="text-sm text-secondary">No se encontraron resultados</p>
              </div>
            )}

            {/* Botón Administrar Empresas */}
            {onAdministrarEmpresas && (
              <div className="p-3 border-t border-secondary mt-3 sticky bottom-0 bg-surface-0">
                <Button
                  id="empresa-selector-administrar"
                  name="empresaSelectorAdministrar"
                  onClick={() => {
                    onAdministrarEmpresas();
                    setIsOpen(false);
                  }}
                  variant="primary"
                  size="md"
                  fullWidth
                  icon={<Settings />}
                  iconPosition="left"
                >
                  Administrar empresas
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

